import { devLog, devError } from '@core';

/**
 * Secure Logger Utility
 *
 * Provides PII-safe logging for production environments.
 * NEVER logs: phone numbers, emails, names, locations, full error objects
 *
 * H-02: Uses dev-logger for production PII protection
 */
export class SecureLogger {
  /**
   * Log error without exposing PII
   *
   * **WI-028**: Type-safe error logging using unknown
   * - Parameter accepts unknown error types from various sources
   * - Only extracts safe properties (message, code) for logging
   * - Used exclusively in dev-logger wrapper (production no-op)
   *
   * @param context - Service or component name
   * @param data - Metadata (uid only, no PII)
   */
  static error(context: string, data: { uid?: string; error?: unknown }): void {
    // Type-safe property extraction
    let message = 'Unknown error';
    let code = 'UNKNOWN';

    if (typeof data.error === 'object' && data.error !== null) {
      if ('message' in data.error) {
        message = String((data.error as { message: unknown }).message);
      }
      if ('code' in data.error) {
        code = String((data.error as { code: unknown }).code);
      }
    }

    devError(`[${context}] Error:`, {
      uid: data.uid || 'unknown',
      message,
      code,
      timestamp: new Date().toISOString(),
      // ❌ NEVER log: full error object, stack traces, user data
    });
  }

  /**
   * Log info message with sanitized data
   *
   * **Why `Record<string, unknown>` is appropriate**:
   * - Accepts arbitrary metadata objects for flexible logging
   * - Sanitization removes PII before passing to dev-logger
   * - GDPR-compliant: PII is stripped before logging
   *
   * @param context - Service or component name
   * @param message - Log message
   * @param data - Metadata (will be sanitized)
   */
  static info(context: string, message: string, data?: Record<string, unknown>): void {
    devLog(`[${context}] ${message}`, this.sanitizeData(data || {}));
  }

  /**
   * Remove PII from log data
   *
   * **Why `Record<string, unknown>` is appropriate**:
   * - Accepts arbitrary objects with unknown property types
   * - Safely removes PII fields without type errors
   * - Returns sanitized object for dev-logger consumption
   *
   * @param data - Data to sanitize
   * @returns Sanitized data
   */
  private static sanitizeData(data: Record<string, unknown>): Record<string, unknown> {
    const sanitized = { ...data };
    // Remove sensitive fields (using bracket notation for TypeScript strict mode)
    delete sanitized['phone'];
    delete sanitized['email'];
    delete sanitized['name'];
    delete sanitized['location'];
    delete sanitized['coordinates'];
    return sanitized;
  }
}
